import { morph } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Preserves rendered B2B pricing, delivery date, and loading states across section morphs
 * to prevent layout reflow / "Calculating..." flashing when modifying cart quantities.
 *
 * @param {HTMLElement} existingElement - The current DOM section element
 * @param {HTMLElement} newElement - The incoming parsed section element
 */
function preserveB2BStateAcrossMorph(existingElement, newElement) {
  if (!existingElement || !newElement) return;

  // 1. Preserve line item unit prices & line totals
  const newRows = newElement.querySelectorAll('[data-key]');
  newRows.forEach((newRow) => {
    const key = newRow.getAttribute('data-key');
    if (!key) return;

    const existingRow = existingElement.querySelector(`[data-key="${key}"]`);
    if (!existingRow) return;

    const existingUnitPrice = existingRow.querySelector('.whiz-b2b-unit-price');
    const newUnitPrice = newRow.querySelector('.whiz-b2b-unit-price');
    if (existingUnitPrice && newUnitPrice) {
      const existingHtml = existingUnitPrice.innerHTML.trim();
      // Only preserve if existing row has an actual price rendered (not "Calculating..." or empty)
      if (existingHtml && !existingHtml.includes('Calculating...')) {
        newUnitPrice.innerHTML = existingHtml;
        newUnitPrice.style.opacity = '0.5';
      }
    }

    const existingLinePrice = existingRow.querySelector('.whiz-b2b-line-price');
    const newLinePrice = newRow.querySelector('.whiz-b2b-line-price');
    if (existingLinePrice && newLinePrice) {
      const existingLineHtml = existingLinePrice.innerHTML.trim();
      if (existingLineHtml) {
        newLinePrice.innerHTML = existingLineHtml;
        newLinePrice.style.opacity = '0.5';
      }
    }
  });

  // 2. Preserve Cart Subtotal / Total value
  const existingTotal = existingElement.querySelector('#whiz-b2b-cart-total-value, .whiz-b2b-cart-total-value');
  const newTotal = newElement.querySelector('#whiz-b2b-cart-total-value, .whiz-b2b-cart-total-value');
  if (existingTotal && newTotal && existingTotal.innerHTML.trim() !== '') {
    newTotal.innerHTML = existingTotal.innerHTML;
    newTotal.style.display = existingTotal.style.display || 'inline-block';
    newTotal.style.opacity = '0.5';
  }

  // 3. Preserve Expected Delivery Date value & row
  const existingDelivery = existingElement.querySelector('#whiz-b2b-delivery-date-value');
  const newDelivery = newElement.querySelector('#whiz-b2b-delivery-date-value');
  if (existingDelivery && newDelivery && existingDelivery.innerHTML.trim() !== '') {
    newDelivery.innerHTML = existingDelivery.innerHTML;
    newDelivery.style.opacity = '0.5';
  }
  const newDeliveryContainer = newElement.querySelector('#cart-drawer-delivery-estimate');
  if (newDeliveryContainer) {
    newDeliveryContainer.style.opacity = '0.5';
  }

  // 4. Preserve Checkout Button state (dimmed & non-interactive during loading)
  const newCheckout = newElement.querySelector('#checkout, button[name="checkout"], .b2b-btn-approved');
  if (newCheckout) {
    newCheckout.style.opacity = '0.5';
    newCheckout.style.pointerEvents = 'none';
  }

  // 5. Preserve Company Discount Notice visibility
  const existingNotice = existingElement.querySelector('.whiz-cart-discount-notice');
  const newNotice = newElement.querySelector('.whiz-cart-discount-notice');
  if (existingNotice && newNotice && existingNotice.style.display === 'block') {
    newNotice.style.display = 'block';
    newNotice.style.opacity = '0.5';
  }
}

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  preserveB2BStateAcrossMorph(existingElement, newElement);

  morph(existingElement, newElement);
  document.dispatchEvent(new CustomEvent('facet-data-updated', { bubbles: true }));
}

export const sectionRenderer = new SectionRenderer();
